/**
 * Device permission brokering for microphone capture.
 *
 * Verified against Microsoft Learn on 2026-08-22 (Office.DevicePermission
 * interface, "Device Permission Prompt API available for Office Add-ins"):
 *
 * - Since 16 May 2024, an add-in running in Word, Excel, PowerPoint or Outlook
 *   **on the web** in a Chromium browser cannot call `getUserMedia` until it has
 *   asked through `Office.devicePermission`. The host iframe is created without
 *   the `microphone` permission-policy feature, so the browser rejects the call
 *   with `NotAllowedError` and never shows its own prompt. That is exactly the
 *   failure the narration panel reports.
 * - `requestPermissions` resolves `true` on a first-time grant, and the add-in
 *   must reload before the capability works: the permission is applied to a
 *   newly created iframe, not to the one that is already running. It resolves
 *   `false` when permission was already granted (no reload needed), and rejects
 *   when the user denies.
 * - The API is **not** available on Office desktop for Windows or Mac, and is
 *   not required there. On those platforms `getUserMedia` reaches WebView2/WKWebView
 *   directly and a denial is an operating-system privacy setting, not an Office one.
 *
 * Because the requirement is platform-specific, so is the answer this module
 * returns — the caller must not present an OS privacy-settings instruction to a
 * user whose real problem is an unanswered Office prompt.
 */
import { isOfficeAvailable } from './capabilities.js';

export type MicrophonePermissionOutcome =
  /** Office desktop or Mac: Office.js does not gate capture, go straight to getUserMedia. */
  | 'not-required'
  /** Granted just now. The add-in must reload before capture will work. */
  | 'granted-needs-reload'
  /** Previously granted and already applied to this frame. */
  | 'already-granted'
  /** The request itself failed. Not proof that capture will fail — see below. */
  | 'refused'
  /** Office on the web, but this build has no DevicePermission API. Try capture anyway. */
  | 'unavailable';

export interface MicrophonePermissionResult {
  readonly outcome: MicrophonePermissionOutcome;
  /**
   * What actually happened, when it was not a clean grant. Carried so the UI can
   * repeat it instead of inventing a cause: a rejection here is reported by
   * Office as a user denial even when no dialog was ever shown, and telling an
   * author they denied a prompt they never saw sends them looking in the wrong
   * place.
   */
  readonly detail?: string;
}

/** True only for Office on the web, where the DevicePermission prompt is mandatory. */
export function isOfficeOnline(): boolean {
  if (!isOfficeAvailable()) return false;
  try {
    return Office.context.platform === Office.PlatformType.OfficeOnline;
  } catch {
    return false;
  }
}

/**
 * Asks Office for microphone access when the platform requires it.
 *
 * Never throws, and a `refused` result is never treated as final by the caller.
 * Two open Office bugs make this API an unreliable authority on the web:
 * office-js#5726 (a granted permission is lost after a refresh or in a new tab
 * for a sideloaded add-in, while the API still answers "already granted") and
 * office-js#5795 (the dialog does not appear at all and the call resolves as if
 * it had). The browser is the authority on whether capture works; this call only
 * gives Office the chance to grant the frame its permission first.
 */
export async function requestMicrophonePermission(): Promise<MicrophonePermissionResult> {
  if (!isOfficeOnline()) return { outcome: 'not-required' };

  const api = (Office as { devicePermission?: Office.DevicePermission }).devicePermission;
  if (typeof api?.requestPermissions !== 'function') {
    return { outcome: 'unavailable', detail: 'This Office build has no DevicePermission API.' };
  }

  const microphone = Office.DevicePermissionType?.microphone;
  if (microphone === undefined) {
    return { outcome: 'unavailable', detail: 'This Office build has no microphone permission type.' };
  }

  try {
    const grantedNow = await api.requestPermissions([microphone]);
    return { outcome: grantedNow ? 'granted-needs-reload' : 'already-granted' };
  } catch (error) {
    return { outcome: 'refused', detail: describeRequestFailure(error) };
  }
}

function describeRequestFailure(error: unknown): string {
  if (error instanceof Error) {
    const name = error.name && error.name !== 'Error' ? `${error.name}: ` : '';
    return `Office refused the permission request (${name}${error.message}).`;
  }
  if (typeof error === 'string' && error.length > 0) {
    return `Office refused the permission request (${error}).`;
  }
  return 'Office refused the permission request without saying why.';
}
